import { Helmet } from "react-helmet-async";

interface BlogSEOProps {
  title: string;
  description: string;
  slug: string;
  date: string;
  author?: string;
  image?: string;
  keywords?: string[];
  lang?: string;
}

const BlogSEO = ({
  title,
  description,
  slug,
  date,
  author = "Fuel72 Team",
  image = "https://www.fuel72.com/fuel72-logo.png",
  keywords = [],
  lang = "en",
}: BlogSEOProps) => {
  const url = `https://www.fuel72.com/blog/${slug}`;
  const fullTitle = `${title} | Fuel72 Blog`;
  const defaultKeywords = [
    "AI UGC",
    "AI UGC video generator",
    "UGC videos",
    "Fuel72",
    "content marketing",
    "business growth",
  ];
  const allKeywords = [...new Set([...keywords, ...defaultKeywords])].join(", ");

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: description,
    image: image,
    author: {
      "@type": "Organization",
      name: author,
      url: "https://www.fuel72.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Fuel72",
      logo: {
        "@type": "ImageObject",
        url: "https://www.fuel72.com/fuel72-logo.png",
      },
    },
    datePublished: date,
    dateModified: date,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.fuel72.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://www.fuel72.com/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: title,
        item: url,
      },
    ],
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Fuel72" />
      <meta property="article:published_time" content={date} />
      <meta property="article:author" content={author} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};

export default BlogSEO;
